import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Slide,
  Avatar,
  CircularProgress,
} from "@mui/material";
import { Close, CloudUpload, Group } from "@mui/icons-material";
import Cropper from "react-easy-crop";
import { compressImage } from "../../utils/ImageCompression"; // Ensure this path is correct
import { updateChatSettings } from "../../services/Chat"; // Ensure this path is correct

// Transition for the Dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Helper to crop image
const getCroppedImage = async (imageSrc, croppedAreaPixels) => {
  const image = new Image();
  image.src = imageSrc;
  await new Promise((resolve) => (image.onload = resolve));

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  canvas.width = croppedAreaPixels.width;
  canvas.height = croppedAreaPixels.height;

  ctx.drawImage(
    image,
    croppedAreaPixels.x,
    croppedAreaPixels.y,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
    0,
    0,
    croppedAreaPixels.width,
    croppedAreaPixels.height
  );

  return new Promise((resolve) => {
    canvas.toBlob(resolve, "image/jpeg");
  });
};

const EditGroupDetails = ({ open, onClose, setChatDetails, chatDetails, setRecentChats, setSelectedChat, getImageUrlWithToken }) => {
  // --- Form State ---
  const [chatName, setChatName] = useState("");
  const [description, setDescription] = useState("");
  const [chatImage, setChatImage] = useState(null);
  const [imageSrc, setImageSrc] = useState(null); // For Cropper
  const [previewImage, setPreviewImage] = useState(null); // For Preview

  // --- Cropper State ---
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // --- Loading States ---
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState(null);

  // Initialize data when modal opens
  useEffect(() => {
    if (open && chatDetails) {
      setChatName(chatDetails.chat_name || "");
      setDescription(chatDetails.description || "");
      setChatImage(chatDetails.chat_image || null);
      setPreviewImage(null);
      setImageSrc(null);
      setErrors(null);
    }
  }, [open, chatDetails]);

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // 1. Handle File Selection
  const handleFileChange = async (e) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result); // This triggers the Cropper UI
    };
    reader.readAsDataURL(file);
  };

  // 2. Handle "Crop & Save" (Internal state update, not API yet)
  const handleCropConfirm = async () => {
    try {
      setIsProcessingImage(true);
      if (imageSrc && croppedAreaPixels) {
        const croppedBlob = await getCroppedImage(imageSrc, croppedAreaPixels);

        // Compress
        let compressedFile = await compressImage(croppedBlob);
        if (compressedFile.size > 500000) {
          compressedFile = await compressImage(compressedFile);
        }

        // Create File Object
        const finalFile = new File([compressedFile], "group_image.jpg", {
          type: "image/jpeg",
          lastModified: new Date().getTime(),
        });
        console.log("Final cropped and compressed file:", finalFile);

        // Update State
        setPreviewImage(URL.createObjectURL(finalFile));
        setChatImage(finalFile);
        setImageSrc(null); // Close Cropper
        
      }
    } catch (e) {
      console.error("Error processing image:", e);
    } finally {
      setIsProcessingImage(false);
    }
  };

  // 3. Final API Submission
  const handleFinalSave = () => {

    if (!chatDetails?.chat_id) return;

    setIsSaving(true);

    const formData = new FormData();
    formData.append("chat_name", chatName);
    formData.append("description", description);
      formData.append("chat_image", chatImage);
  

    console.log("Submitting form data:", {
      chat_name: chatName,
      description,
      chat_image: chatImage,
    });

    updateChatSettings(chatDetails.chat_id, formData)
      .then((res) => {
        console.log("Chat Settings Updated", res);
        // Update local state with new details
        const updatedChat = res.chat;
        setChatDetails((prevDetails) => ({
          ...prevDetails,
          chat_name: updatedChat.chat_name,
          description: updatedChat.description,
          chat_image: updatedChat.chat_image,
        }));

        // Update recent chats list
        setRecentChats((prevChats) =>
          prevChats.map((chat) =>
            chat.chat_id === chatDetails.chat_id
              ? { ...chat, chat_name: updatedChat.chat_name, chat_image: updatedChat.chat_image }
              : chat
          )
        );
        // Update selected chat details
        setSelectedChat((prevChat) =>
          prevChat && prevChat.chat_id === chatDetails.chat_id
            ? { ...prevChat, chat_name: updatedChat.chat_name, chat_image: updatedChat.chat_image }
            : prevChat
        );

        onClose(); // Close modal
      })
      .catch((error) => {
        console.error("Chat Settings Update Error", error);
        // Optional: Show error toast here
        setErrors(error.errors);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      TransitionComponent={Transition}
    >
      <DialogTitle sx={{ bgcolor: "background.paper" }}>
        Edit Group Details
      </DialogTitle>

      <IconButton
        onClick={onClose}
        sx={{ position: "absolute", top: 10, right: 10 }}
      >
        <Close />
      </IconButton>

      <DialogContent sx={{ bgcolor: "background.paper" }}>
        {/* --- VIEW 1: FORM & PREVIEW --- */}
        {!imageSrc ? (
          <>
            {/* Image Preview & Upload Button */}
            <Box
              sx={{
                width: 100,
                height: 100,
                margin: "0 auto",
                mb: 2,
                position: "relative",
              }}
            >
              <Avatar
                src={previewImage || getImageUrlWithToken(chatImage)}
                alt={chatName}
                sx={{
                  width: 100,
                  height: 100,
                  border: "1px solid #ddd",
                  fontSize: 40
                }}
              >
               <Group sx={{ fontSize: 60 }} />
              </Avatar>
              <IconButton
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  bgcolor: "background.paper",
                }}
                onClick={() => setChatImage(null)}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
              <Button
                variant="outlined"
                component="label"
                size="small"
                startIcon={<CloudUpload />}
                sx={{ textTransform: "none", borderRadius: 20 }}
              >
                Change Image
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </Button>
            </Box>

            {/* Inputs */}
            <TextField
              label="Group Name"
              fullWidth
              size="small"
              margin="normal"
              value={chatName}
              onChange={(e) => setChatName(e.target.value)}
              error={Boolean(errors?.chat_name)}
              helperText={errors?.chat_name?.[0] || ""}
            />

            <TextField
              label="Description"
              fullWidth
              size="small"
              margin="normal"
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              error={Boolean(errors?.description)}
              helperText={errors?.description?.[0] || ""}
            />
          </>
        ) : (
          /* --- VIEW 2: CROPPER --- */
          <Box sx={{ position: "relative", width: "100%", height: 300, mb: 2 }}>
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ bgcolor: "background.paper", px: 3, pb: 3 }}>
        {imageSrc ? (
          // Actions when Cropping
          <>
            <Button
              onClick={() => setImageSrc(null)}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCropConfirm}
              variant="contained"
              disabled={isProcessingImage}
              sx={{ textTransform: "none" }}
            >
              {isProcessingImage ? <CircularProgress size={24} /> : "Crop & Set"}
            </Button>
          </>
        ) : (
          // Actions when Editing Text
          <>
            <Button
              onClick={onClose}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleFinalSave}
              variant="contained"
              disabled={isSaving}
              sx={{ textTransform: "none" }}
            >
              {isSaving ? <CircularProgress size={24} color="inherit" /> : "Save Changes"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EditGroupDetails;